/**
 * LayoutModeContext
 *
 * Controls whether the page layout shows chrome (Header, Footer) or runs bare
 * (full-screen, no chrome).
 *
 * Two mechanisms, both honoured by LocaleLayout / pages/+Layout:
 *
 *   1. **Route metadata** (declarative) — Vike `urlLogical` starting with `/lp/`
 *      (or shim `useMatches()[0].handle.bare`) marks the route bare.
 *
 *   2. **Runtime context** (imperative) — any page component can call
 *      `useLayoutMode().setBare(true)` to hide chrome dynamically (e.g. when
 *      a funnel experience activates).
 *
 * If EITHER source says bare, chrome is hidden.
 */

import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';

interface LayoutModeState {
  /** True when chrome (Header/Footer) should be hidden. */
  bare: boolean;
  /** Imperatively set bare mode from any child component. */
  setBare: (value: boolean) => void;
  /** Internal flag — truthy only when the provider is mounted. */
  __providerMounted: true;
}

// The default context value is intentionally missing __providerMounted so that
// useLayoutMode can detect when the hook is used outside a LayoutModeProvider.
const LayoutModeContext = createContext<LayoutModeState | undefined>(undefined);

export function LayoutModeProvider({
  routeBare,
  children,
}: {
  /** Whether the matched route declared `handle.bare = true`. */
  routeBare: boolean;
  children: ReactNode;
}) {
  const [runtimeBare, setRuntimeBare] = useState(false);

  const setBare = useCallback((value: boolean) => setRuntimeBare(value), []);

  const value = useMemo<LayoutModeState>(
    () => ({ bare: routeBare || runtimeBare, setBare, __providerMounted: true }),
    [routeBare, runtimeBare, setBare],
  );

  return (
    <LayoutModeContext.Provider value={value}>
      {children}
    </LayoutModeContext.Provider>
  );
}

export function useLayoutMode(): LayoutModeState {
  const context = useContext(LayoutModeContext);
  if (context === undefined) {
    throw new Error('useLayoutMode must be used within a LayoutModeProvider');
  }
  return context;
}
